"use client";
import { useEffect, useState } from "react";
/**
 * useSelfProfile — client hook that resolves the current fleet identity.
 *
 * On mount (and whenever `selfProfileUrl` changes) it does a CREDENTIALED
 * cross-subdomain GET to the accounts self-profile endpoint; the parent-domain
 * `owlcat-id` cookie rides along automatically, so a session started in ANY
 * sibling app is reflected. Failure modes all collapse to `anon` so the host
 * page never breaks:
 *
 *   • 200 with `identityId` → `{ status: 'user', profile }`
 *   • 401 / 404 / any non-OK → `{ status: 'anon' }`
 *   • offline / CORS-blocked / DNS (fetch throws) → `{ status: 'anon' }`
 *
 * `markSignedOut()` lets the caller flip the state to `anon` after a sign-out
 * without re-fetching.
 *
 * `knownAnonymous` (optional): the server saw no fleet identity cookie → `anon` at once, no
 * request (an anonymous visitor otherwise logs a 401 on every page view).
 *
 * `appId` (optional): when supplied it is sent as the `X-Owlcat-App` request
 * header so accounts can best-effort stamp identity_app_usage on use — apps that
 * verify the owlcat-id cookie LOCALLY (no accounts round-trip) otherwise never
 * report they were opened. Absent → behaves exactly as before (no header).
 */
export function useSelfProfile(selfProfileUrl, appId, initialProfile, knownAnonymous) {
    // the app's server-verified user is the first truth (no flicker); accounts /me refines it
    const seed = initialProfile?.identityId ? { status: "user", profile: initialProfile } : knownAnonymous ? { status: "anon" } : { status: "loading" };
    const [state, setState] = useState(seed);
    useEffect(() => {
        let alive = true;
        if (knownAnonymous && !initialProfile?.identityId) {
            setState({ status: "anon" });
            return;
        }
        // keep the seeded user on screen while /me is in flight; only an unseeded header shows "loading"
        if (!initialProfile?.identityId)
            setState({ status: "loading" });
        (async () => {
            try {
                const headers = { Accept: "application/json" };
                if (appId)
                    headers["X-Owlcat-App"] = appId;
                const res = await fetch(selfProfileUrl, {
                    credentials: "include",
                    headers,
                });
                if (!alive)
                    return;
                if (!res.ok) {
                    // 401/403 = accounts says the login ended → signed out everywhere (the app's next
                    // request drops its native session too). Any other failure keeps the app's own user.
                    if (res.status === 401 || res.status === 403 || !initialProfile?.identityId)
                        setState({ status: "anon" });
                    return;
                }
                const profile = (await res.json());
                if (!alive)
                    return;
                setState(profile && profile.identityId
                    ? { status: "user", profile }
                    : { status: "anon" });
            }
            catch {
                // Offline / CORS-blocked / DNS — the app's own user stays; nobody known → signed-out.
                if (alive && !initialProfile?.identityId)
                    setState({ status: "anon" });
            }
        })();
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selfProfileUrl, appId, initialProfile?.identityId, knownAnonymous]);
    return { state, markSignedOut: () => setState({ status: "anon" }) };
}
